import * as React from 'react'

import mapboxgl from 'mapbox-gl'
import { Layer, MapRef, Source } from 'react-map-gl/mapbox'

import Config from '~/client/src/shared/Config'
import {
  DEFAULT_PROJECTION,
  STREET_STYLE_CODE,
} from '~/client/src/shared/map/mapBasemapRule'

import MapCanvas from '../MapBoxViewer/MapCanvas'
import { TEXT_OCCLUSION_PAINT } from '../MapBoxViewer/mapOcclusionRule'

interface IPoint {
  latitude: number
  longitude: number
}

interface ISegment {
  points: IPoint[]
}

interface IProps {
  segments: ISegment[]
}

const PATH_SOURCE_ID = 'strux-tag-gps-path'
const PATH_LAYER_ID = 'strux-tag-gps-path-line'
const PATH_DIRECTION_LAYER_ID = 'strux-tag-gps-path-direction'
const ENDPOINTS_SOURCE_ID = 'strux-tag-gps-path-endpoints'
const ENDPOINTS_LAYER_ID = 'strux-tag-gps-path-endpoints-circle'
const ENDPOINT_LABELS_LAYER_ID = 'strux-tag-gps-path-endpoint-labels'

const getBounds = (points: IPoint[]): mapboxgl.LngLatBounds => {
  const first = points[0]
  const bounds = new mapboxgl.LngLatBounds(
    [first.longitude, first.latitude],
    [first.longitude, first.latitude],
  )

  points.forEach(point => {
    bounds.extend([point.longitude, point.latitude])
  })

  return bounds
}

const StruxTagGpsPathMap: React.FC<IProps> = ({ segments }) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const mapRef = React.useRef<MapRef | null>(null)
  const [isStyleLoaded, setIsStyleLoaded] = React.useState(false)
  const [containerSizeKey, setContainerSizeKey] = React.useState('')
  const fittedPathKeyRef = React.useRef<string | null>(null)
  const hasUserInteractedRef = React.useRef(false)
  const validSegments = React.useMemo(() => {
    return segments
      .map(segment => ({
        points: segment.points.filter(point => {
          return (
            Number.isFinite(point.latitude) && Number.isFinite(point.longitude)
          )
        }),
      }))
      .filter(segment => segment.points.length > 0)
  }, [segments])
  const validPoints = React.useMemo(() => {
    return validSegments.flatMap(segment => segment.points)
  }, [validSegments])
  const firstPoint = validSegments[0]?.points[0]
  const lastSegment = validSegments[validSegments.length - 1]
  const lastPoint = lastSegment?.points[lastSegment.points.length - 1]
  const pathKey = firstPoint
    ? `${validPoints.length}:${firstPoint.longitude},${firstPoint.latitude}:${lastPoint?.longitude},${lastPoint?.latitude}`
    : ''

  const pathGeoJson = React.useMemo(() => {
    return {
      type: 'FeatureCollection',
      features: validSegments
        .filter(segment => segment.points.length > 1)
        .map(segment => ({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: segment.points.map(point => [
              point.longitude,
              point.latitude,
            ]),
          },
        })),
    }
  }, [validSegments])

  const endpointsGeoJson = React.useMemo(() => {
    return {
      type: 'FeatureCollection',
      features: [
        { point: firstPoint, label: 'Start' },
        { point: lastPoint, label: 'End' },
      ]
        .filter(({ point }) => !!point)
        .map(({ point, label }) => ({
          type: 'Feature',
          properties: { label },
          geometry: {
            type: 'Point',
            coordinates: [point.longitude, point.latitude],
          },
        })),
    }
  }, [firstPoint, lastPoint])

  const fitPathBounds = React.useCallback(() => {
    const map = mapRef.current?.getMap()
    const container = containerRef.current
    if (
      !map ||
      !container ||
      !isStyleLoaded ||
      hasUserInteractedRef.current ||
      validPoints.length < 2 ||
      fittedPathKeyRef.current === pathKey
    ) {
      return
    }

    const { width, height } = container.getBoundingClientRect()
    if (width < 100 || height < 100) return

    map.resize()
    map.fitBounds(getBounds(validPoints), {
      padding: 24,
      duration: 0,
      maxZoom: 17,
    })
  }, [isStyleLoaded, pathKey, validPoints])

  React.useEffect(() => {
    fittedPathKeyRef.current = null
    hasUserInteractedRef.current = false
  }, [pathKey])

  React.useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0]
      if (!entry) return

      setContainerSizeKey(
        `${Math.round(entry.contentRect.width)}:${Math.round(
          entry.contentRect.height,
        )}`,
      )
    })
    resizeObserver.observe(container)
    return () => resizeObserver.disconnect()
  }, [])

  React.useEffect(() => {
    if (!isStyleLoaded || !containerSizeKey) return

    const frame = window.requestAnimationFrame(fitPathBounds)
    const firstTimeout = window.setTimeout(fitPathBounds, 100)
    const secondTimeout = window.setTimeout(() => {
      fitPathBounds()
      fittedPathKeyRef.current = pathKey
    }, 350)

    return () => {
      window.cancelAnimationFrame(frame)
      window.clearTimeout(firstTimeout)
      window.clearTimeout(secondTimeout)
    }
  }, [containerSizeKey, fitPathBounds, isStyleLoaded, pathKey])

  if (validPoints.length < 2 || !firstPoint) {
    return null
  }

  return (
    <div
      ref={containerRef}
      className="tw:h-52 tw:overflow-hidden tw:rounded-lg tw:border tw:border-solid tw:border-border"
    >
      <MapCanvas
        ref={mapRef}
        initialViewState={{
          longitude: firstPoint.longitude,
          latitude: firstPoint.latitude,
          zoom: 14,
        }}
        style={{ width: '100%', height: '100%' }}
        mapStyle={STREET_STYLE_CODE}
        projection={DEFAULT_PROJECTION}
        mapboxAccessToken={mapboxgl.accessToken || Config.MAPBOX_TOKEN}
        onStyleLoadedChange={setIsStyleLoaded}
        onMoveStart={() => {
          if (fittedPathKeyRef.current === pathKey) {
            hasUserInteractedRef.current = true
          }
        }}
      >
        {isStyleLoaded && (
          <>
            <Source
              id={PATH_SOURCE_ID}
              type="geojson"
              data={pathGeoJson as any}
            >
              <Layer
                id={PATH_LAYER_ID}
                type="line"
                paint={{
                  'line-color': '#2563eb',
                  'line-width': 4,
                  'line-opacity': 0.9,
                }}
                layout={{
                  'line-cap': 'round',
                  'line-join': 'round',
                }}
              />
              <Layer
                id={PATH_DIRECTION_LAYER_ID}
                type="symbol"
                layout={{
                  'symbol-placement': 'line',
                  'symbol-spacing': 80,
                  'text-field': '▶',
                  'text-keep-upright': false,
                  'text-size': 14,
                }}
                paint={{
                  'text-color': '#1d4ed8',
                  'text-halo-color': '#ffffff',
                  'text-halo-width': 1,
                  ...TEXT_OCCLUSION_PAINT,
                }}
              />
            </Source>
            <Source
              id={ENDPOINTS_SOURCE_ID}
              type="geojson"
              data={endpointsGeoJson as any}
            >
              <Layer
                id={ENDPOINTS_LAYER_ID}
                type="circle"
                paint={{
                  'circle-color': [
                    'match',
                    ['get', 'label'],
                    'Start',
                    '#16a34a',
                    '#2563eb',
                  ],
                  'circle-radius': 5,
                  'circle-stroke-color': '#ffffff',
                  'circle-stroke-width': 2,
                }}
              />
              <Layer
                id={ENDPOINT_LABELS_LAYER_ID}
                type="symbol"
                layout={{
                  'text-field': ['get', 'label'],
                  'text-size': 12,
                  'text-offset': [0, 1.2],
                  'text-anchor': 'top',
                }}
                paint={{
                  'text-color': '#111827',
                  'text-halo-color': '#ffffff',
                  'text-halo-width': 1,
                  ...TEXT_OCCLUSION_PAINT,
                }}
              />
            </Source>
          </>
        )}
      </MapCanvas>
    </div>
  )
}

export default StruxTagGpsPathMap
